import {ChangeDetectionStrategy, Component, computed, effect, inject, signal, viewChild} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {Store} from '@ngrx/store';
import {SeFeIconModule} from 'se-fe-icon';
import {SeFeButtonModule} from 'se-fe-button';
import {SeFeModalButton, SeFeModalComponent, SeFeModalModule} from 'se-fe-modal';
import {SeFeSpinnerModule} from 'se-fe-spinner';
import {CatalogSummary} from '../../../models/clare';
import {CatalogActions} from '../store/catalog.actions';
import {selectCatalogRegistrationsRequestState, selectDeleteCatalogRequestState} from '../store/catalog.selectors';

@Component({
  selector: 'app-delete-catalog-modal',
  templateUrl: './delete-catalog-modal.component.html',
  styleUrl: './delete-catalog-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SeFeButtonModule, SeFeIconModule, SeFeModalModule, SeFeSpinnerModule],
})
export class DeleteCatalogModalComponent {
  readonly deleteCatalogModal = viewChild.required<SeFeModalComponent>('deleteCatalogModal');
  readonly selectedCatalog = signal<CatalogSummary | undefined>(undefined);

  private readonly store = inject(Store);
  private readonly deleteCatalogRequestState = toSignal(this.store.select(selectDeleteCatalogRequestState), {
    requireSync: true,
  });
  readonly catalogRegistrationsRequestState = toSignal(this.store.select(selectCatalogRegistrationsRequestState), {
    requireSync: true,
  });

  readonly deleteCatalogModalButtons = computed<SeFeModalButton[]>(() => [
    {
      cancel: true,
      label: 'Cancel',
      disabled: this.deleteCatalogRequestState().status === 'loading',
      action: () => this.close(),
    },
    {
      label: 'Delete',
      destructive: true,
      icon: true,
      iconName: 'trash',
      disabled:
        this.deleteCatalogRequestState().status === 'loading' ||
        this.catalogRegistrationsRequestState().status !== 'success',
      loadingLabel: 'Deleting catalog',
      action: () => this.deleteCatalog(),
    },
  ]);

  constructor() {
    effect(() => {
      const {catalogId, status} = this.deleteCatalogRequestState();

      if (status === 'success' && catalogId === this.selectedCatalog()?.id) {
        this.close();
      }
    });
  }

  open(catalog: CatalogSummary, trigger: HTMLElement): void {
    this.store.dispatch(CatalogActions.resetDeleteCatalogStatus());
    this.selectedCatalog.set(catalog);
    this.loadCatalogRegistrations(catalog);
    this.deleteCatalogModal().open(trigger);
  }

  deleteCatalog(): void {
    const catalog = this.selectedCatalog();

    if (!catalog) {
      return;
    }

    this.store.dispatch(CatalogActions.deleteCatalog({catalogId: catalog.id}));
  }

  close(): void {
    if (this.deleteCatalogRequestState().status === 'loading') {
      return;
    }

    this.deleteCatalogModal().close();
    this.selectedCatalog.set(undefined);
    this.store.dispatch(CatalogActions.resetDeleteCatalogStatus());
    this.store.dispatch(CatalogActions.resetCatalogRegistrations());
  }

  retryCatalogRegistrations(): void {
    this.loadCatalogRegistrations(this.selectedCatalog()!);
  }

  private loadCatalogRegistrations(catalog: CatalogSummary): void {
    this.store.dispatch(
      CatalogActions.loadCatalogRegistrations({
        catalogId: catalog.id,
        catalogKey: catalog.key!,
      })
    );
  }
}
